import React, { useEffect, useState, useCallback } from 'react';
import { RouteProps, useHistory } from 'react-router-dom';
import { ServiceHistory } from '../../components/sections';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import AuthenticationService from '../../services/authentication-service/authentication-service';
import { ActivityIndicator } from '../../components/common/activity-indicator/activity-indicator';
import ServerSideService from '../../services/server-side-service/server-side-service';
import AppConfigurationService from '../../services/app-configuration-service/app-configuration-service';
import { PageType } from '../../components/sections/view-template/page-type';
import { useAnalytics } from '../../hooks/use-analytics';
import useUserProfile from '../../hooks/use-user-profile';
import useSeoConfig from '../../hooks/use-seo-config';
import ServiceHandler from '../../services/service-handler';
import { ServiceRecord } from '../../models/service-history';

export function ServiceHistoryView(props: RouteProps) {
    const [fireEvents] = useAnalytics();
    const selectedVin = ServerSideService.isClientSide()
        ? window.sessionStorage.getItem('selectedVin')
        : false;
    const [
        vehicleAttributes,
        setVehicleAttributes,
    ] = useState<VehicleAttributes | null>(null);
    const [vehiclesData, setVehiclesData] = useState<VehicleAttributes[]>([]);
    const [records, setRecords] = useState<any[]>([]);
    const [apiCompleted, setApiCompleted] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const authenticationService = new AuthenticationService();
    const profile = useUserProfile();
    const [userGuid, setUserGuid] = useState<string>('');
    const history = useHistory();
    const goToHomePage = useCallback(() => {
        history.push('/');
    }, [history]);
    const routeState = props.location?.state as
        | { fromPage: PageType }
        | undefined;
    const appConfig = new AppConfigurationService();
    const [uomDistance, setUomDistance] = useState<number>(0);
    useSeoConfig('service-history', 'service-history');
    useEffect(() => {
        const vehicleAttributesSession = sessionStorage.getItem(
            'vehicleAttributes'
        );

        if (profile) {
            if (profile.profile) {
                setUserGuid(profile.profile.userGuid);
                setUomDistance(profile.profile.uomDistance);
            }
            if (
                appConfig.isRegionEU() &&
                routeState?.fromPage === 'VIN' &&
                vehicleAttributesSession
            ) {
                setVehicleAttributes(JSON.parse(vehicleAttributesSession));
            } else if (profile.vehicles.length > 0) {
                setVehiclesData(
                    profile.vehicles.map(vehicle => {
                        return {
                            year: parseInt(vehicle.modelYear, 10),
                            make: vehicle.make,
                            model: vehicle.modelName,
                            vin: vehicle.vin,
                            uomSpeed: profile.profile.uomSpeed,
                        };
                    })
                );
            } else {
                goToHomePage();
            }
        } else if (
            appConfig.isRegionEU() &&
            routeState?.fromPage === 'VIN' &&
            vehicleAttributesSession
        ) {
            setVehicleAttributes(JSON.parse(vehicleAttributesSession));
        } else {
            authenticationService.onIsAuthenticated().then(authenticated => {
                !authenticated && authenticationService.login();
            });
        }
    }, [profile, goToHomePage]);
    const triggerLoadAnalytics = (selectedVehicleData: any) => {
        if (selectedVin) {
            fireEvents(
                ['target-trigger-view', 'service-history'],
                'service-history',
                { ymm: Promise.resolve(selectedVehicleData) },
                false
            );
        }
    };
    useEffect(() => {
        if (vehiclesData.length > 0) {
            const selectedVehicleData =
                vehiclesData.find(vehicle => vehicle.vin === selectedVin) ||
                vehiclesData[0];
            triggerLoadAnalytics(selectedVehicleData);
            setVehicleAttributes(
                selectedVin ? selectedVehicleData : vehiclesData[0]
            );
        }
    }, [vehiclesData]);

    useEffect(() => {
        if (vehicleAttributes) {
            getServiceHistoryRecords();
        }
    }, [vehicleAttributes]);

    const getServiceHistoryRecords = async () => {
        if (vehicleAttributes?.vin) {
            try {
                const value = await ServiceHandler.VehicleServiceHistory.getVehicleServiceHistory(
                    vehicleAttributes?.vin
                );
                setRecords(transformResponse(value));
                setApiCompleted(true);
                setShowError(false);
            } catch (error) {
                setApiCompleted(false);
                setShowError(true);
                console.error('Error fetching service history:', error);
            }
        }
    };

    const transformResponse = (response: {
        userServices: Array<ServiceRecord>;
        dealerServices: Array<ServiceRecord>;
    }) => {
        const userServicesData = response.userServices.map(
            (userData: { serviceDate: string }) => ({
                ...userData,
                serviceCategory: 'owner',
                date: new Date(userData.serviceDate),
            })
        );
        const dealerServicesData = response.dealerServices.map(
            (dealerData: { serviceDate: string }) => ({
                ...dealerData,
                serviceCategory: 'dealer',
                date: new Date(dealerData.serviceDate),
            })
        );
        const mergeServicesData = [...userServicesData, ...dealerServicesData];
        const sortedData = [...mergeServicesData].sort((a, b) => {
            if (a.date === b.date) return 0;
            if (a.date < b.date) return -1;
            else return 1;
        });
        return sortedData;
    };

    return (
        <>
            {vehicleAttributes ? (
                <ServiceHistory
                    vehicleAttributes={vehicleAttributes}
                    records={records}
                    fetchServiceHistoryRecords={getServiceHistoryRecords}
                    userGuid={userGuid}
                    uomDistance={uomDistance}
                    page={'Service History'}
                    isApiCompleted={apiCompleted}
                    showError={showError}
                />
            ) : (
                <ActivityIndicator className="full-height" />
            )}
        </>
    );
}
export default ServiceHistoryView;
